import { useHandler } from "@redotech/react-util/hook";
import TriangleDown from "@redotech/redo-icons/triangle-down_filled.svg";
import TriangleRight from "@redotech/redo-icons/triangle-right_filled.svg";
import { AnimatePresence, motion } from "motion/react";
import {
  cloneElement,
  isValidElement,
  memo,
  ReactElement,
  ReactNode,
  useId,
  useState,
} from "react";
import { Flex } from "../../flex";
import { Icon } from "../../icon";
import type { SpacingValue } from "../../theme/box";
import { RedoButton } from "../buttons/redo-button";

export const RedoCollapseSubsection = memo(function RedoCollapseSubsection({
  children,
  title,
  tooltip,
  defaultCollapsed = true,
  gap = "md",
  renderHeader,
  "data-dd-action-name": actionName,
}: {
  children: ReactNode | ReactNode[];
  title: string;
  tooltip?: ReactNode | ReactNode[];
  defaultCollapsed?: boolean;
  gap?: SpacingValue;
  renderHeader?: (props: {
    collapsed: boolean;
    leadingIcon: ReactNode;
    onClick: () => void;
  }) => ReactElement;
  "data-dd-action-name"?: string;
}) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  const contentId = useId();

  const onClick = useHandler(() => setCollapsed((value) => !value));

  const leadingIcon = () => (
    <Icon
      arbiterIconSvg={collapsed ? TriangleRight : TriangleDown}
      color="ghost"
      style={{ width: 14, height: 14 }}
    />
  );

  // The disclosure ARIA attributes are applied here rather than handed to
  // `renderHeader`, so a custom header never has to wire up (or even know about)
  // the internal content id.
  const ariaProps = { "aria-controls": contentId, "aria-expanded": !collapsed };

  const header = renderHeader?.({
    collapsed,
    leadingIcon: leadingIcon(),
    onClick,
  });

  return (
    <Flex
      data-arbiter="RedoCollapseSubsection"
      data-dd-action-name={actionName}
      dir="column"
      gap={gap}
    >
      {header ? (
        isValidElement(header) ? (
          cloneElement(header, ariaProps)
        ) : (
          header
        )
      ) : (
        <Flex>
          <RedoButton
            data-dd-action-name={actionName}
            {...ariaProps}
            color="gray"
            hierarchy="link"
            IconLeading={leadingIcon}
            onClick={onClick}
            size="sm"
            text={title}
          />
          {tooltip}
        </Flex>
      )}
      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            id={contentId}
            initial={{ height: 0, opacity: 0 }}
            style={{ overflow: "hidden" }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </Flex>
  );
});
